import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";
import { Link } from "react-router-dom";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const logoTextColor = isScrolled ? "text-foreground-900" : "text-white";
  const hamburgerColor = isScrolled ? "text-foreground-900" : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className={`font-heading text-xl md:text-2xl tracking-tight transition-colors duration-300 ${logoTextColor}`}>
              BaseTao<span className="text-primary-400">Sheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? "text-foreground-700 hover:bg-background-100"
                  : "text-white/85 hover:bg-white/10"
              }`}
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? "bg-background-100 hover:bg-background-200 text-foreground-700"
                    : "bg-white/15 hover:bg-white/25 text-white"
                }`}
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 border border-background-200/70 rounded-xl shadow-lg py-2 max-h-80 overflow-y-auto z-50 grid grid-cols-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <span className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center shrink-0">
                        <i className="ri-shopping-bag-3-line text-primary-500 text-xs"></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/basetao-shopping-guide"
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? "bg-accent-50 text-accent-700 hover:bg-accent-100"
                  : "bg-white/15 hover:bg-white/25 text-white"
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled
                  ? "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
                  : "bg-white/20 hover:bg-white/30 text-white"
              }`}
            >
              BaseTao Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled ? "hover:bg-background-100" : "hover:bg-white/10"
            }`}
          >
            <i className={`text-xl transition-colors duration-300 ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} ${hamburgerColor}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className={`md:hidden border-t ${isScrolled ? "border-background-200/60" : "border-white/15"} py-4 space-y-1`}>
            <Link
              to="/"
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm rounded-lg transition-colors ${
                isScrolled ? "text-foreground-700 hover:bg-primary-50" : "text-white/90 hover:bg-white/10"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center shrink-0">
                <i className="ri-home-line text-primary-500 text-xs"></i>
              </span>
              Home
            </Link>

            <Link
              to="/article/basetao-shopping-guide"
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm rounded-lg transition-colors ${
                isScrolled ? "text-foreground-700 hover:bg-accent-50" : "text-white/90 hover:bg-white/10"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-6 h-6 rounded bg-accent-50 flex items-center justify-center shrink-0">
                <i className="ri-book-open-line text-accent-600 text-xs"></i>
              </span>
              Shopping Guide
            </Link>

            <div className={`my-2 border-t ${isScrolled ? "border-background-200/40" : "border-white/10"}`} />

            <p className={`px-4 py-1 text-xs font-medium ${isScrolled ? "text-foreground-500" : "text-white/50"}`}>PRODUCT CATEGORIES</p>

            <div className="grid grid-cols-2 gap-1 px-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors ${
                    isScrolled ? "text-foreground-600 hover:bg-primary-50 hover:text-primary-700" : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="w-5 h-5 rounded bg-primary-50/70 flex items-center justify-center shrink-0">
                    <i className="ri-shopping-bag-3-line text-primary-500 text-[10px]"></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className={`my-2 border-t ${isScrolled ? "border-background-200/40" : "border-white/10"}`} />

            <div className="flex flex-col gap-2 px-4 pt-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`w-full px-4 py-2.5 rounded-lg text-sm font-medium text-center transition-colors cursor-pointer whitespace-nowrap ${
                  isScrolled
                    ? "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                BaseTao Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-2.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 text-sm font-semibold text-center transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}